import { Component, type ErrorInfo, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallbackText: string;
  inline?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class MathErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.warn("MathText rendering fallback triggered:", error, info);
  }

  render() {
    if (this.state.hasError) {
      return this.props.inline ? (
        <span className="font-mono text-inherit">{this.props.fallbackText}</span>
      ) : (
        <div className="font-mono text-inherit whitespace-pre-wrap">{this.props.fallbackText}</div>
      );
    }
    return this.props.children;
  }
}

export interface MathTextProps {
  children?: string | null;
  className?: string;
  inline?: boolean;
}

/**
 * Shared MathText component for questions, options, explanations, notes, titles,
 * past exams, and admin previews.
 * - Uses react-markdown + remark-math + rehype-katex + KaTeX CSS
 * - Only $...$ (inline) and $$...$$ (display)
 * - Never truncates, escapes, or trims the input string before rendering
 * - On parse error, displays raw LaTeX without breaking surrounding text
 */
export function MathText({ children, className = "", inline = false }: MathTextProps) {
  // Never truncate, escape or trim before rendering
  const content = children == null ? "" : String(children);
  if (!content) return null;

  const katexOptions = {
    throwOnError: false,
    strict: false,
    errorColor: "var(--destructive)",
  };

  if (inline) {
    return (
      <MathErrorBoundary fallbackText={content} inline>
        <span className={`math-text math-text--inline ${className}`.trim()}>
          <ReactMarkdown
            remarkPlugins={[remarkMath]}
            rehypePlugins={[[rehypeKatex, katexOptions]]}
            components={{
              p: ({ children: c }) => <span>{c}</span>,
            }}
          >
            {content}
          </ReactMarkdown>
        </span>
      </MathErrorBoundary>
    );
  }

  return (
    <MathErrorBoundary fallbackText={content} inline={false}>
      <div className={`math-text ${className}`.trim()}>
        <ReactMarkdown
          remarkPlugins={[remarkMath]}
          rehypePlugins={[[rehypeKatex, katexOptions]]}
        >
          {content}
        </ReactMarkdown>
      </div>
    </MathErrorBoundary>
  );
}
